/**
 * `Section` and `Field` - presentation primitives for the detail page.
 *
 * Each `Section` is a card with an `aria-labelledby` heading; each
 * `Field` renders a single `<dt>` / `<dd>` pair inside the section's `<dl>`.
 * Per the component index in this document, the detail page is structured
 * semantically so screen readers can navigate by section heading and so
 * the field list reads as a description list rather than a plain table.
 *
 * The Field renders a `ReactNode` so callers can drop in plain strings,
 * monospace code blocks, status pills, or the MaskedField primitive.
 */
import { useId } from 'react'
import type { ReactNode } from 'react'

export interface SectionProps {
  readonly title: string
  readonly children: ReactNode
  /** Optional rich heading content (e.g. helper link). */
  readonly headingExtra?: ReactNode
  /**
   * Field columns at desktop widths. Sections placed in the detail page's
   * 340px rail must be single-column - two columns of labels in 340px wraps
   * every value. Defaults to 2 for sections in the wide column.
   */
  readonly columns?: 1 | 2
  /**
   * Caption rendered below the field list. Use for guidance about the card
   * as a whole, such as the audited-reveal warning on the Holder card.
   *
   * It renders *outside* the `<dl>`: a `<dl>` may only directly contain
   * `<dt>`, `<dd>`, `<div>`, `<script>` and `<template>`, so a caption
   * dropped in beside the fields is invalid markup and a serious axe
   * violation (`definition-list`).
   */
  readonly footnote?: ReactNode
}

export function Section({
  title,
  children,
  headingExtra,
  columns = 2,
  footnote,
}: SectionProps): JSX.Element {
  const headingId = useId()
  return (
    <section
      aria-labelledby={headingId}
      className="rounded-md border border-border-default bg-bg-surface p-murmuration-4 shadow-elev-1"
    >
      <header className="mb-murmuration-3 flex items-center justify-between gap-murmuration-3">
        <h2 id={headingId} className="text-murmuration-h3 text-text-primary">
          {title}
        </h2>
        {headingExtra}
      </header>
      <dl
        className={`grid grid-cols-1 gap-x-murmuration-5 gap-y-murmuration-3 ${
          columns === 2 ? 'md:grid-cols-2' : ''
        }`}
      >
        {children}
      </dl>
      {footnote !== undefined && (
        <p className="mt-murmuration-3 text-murmuration-label text-text-faint">{footnote}</p>
      )}
    </section>
  )
}

export interface FieldProps {
  readonly label: string
  readonly children: ReactNode
  /** Optional id forwarded onto the `<dd>` so the value is referenceable. */
  readonly valueId?: string
}

export function Field({ label, children, valueId }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-murmuration-1">
      <dt className="text-murmuration-label uppercase tracking-wide text-text-muted">{label}</dt>
      <dd id={valueId} className="text-murmuration-body text-text-primary">
        {children}
      </dd>
    </div>
  )
}

/** Render a value or an `n/a` placeholder when null. */
export function FieldValue({ value }: { readonly value: string | number | null }): JSX.Element {
  if (value === null || value === '' || value === undefined) {
    return <span className="text-text-muted">n/a</span>
  }
  return <span>{String(value)}</span>
}

/** Render an ISO-8601 timestamp as `YYYY-MM-DD HH:mm UTC`. */
export function Timestamp({ value }: { readonly value: string | null }): JSX.Element {
  if (value === null || value === '') {
    return <span className="text-text-muted">n/a</span>
  }
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return <span>{value}</span>
    const pad = (n: number) => String(n).padStart(2, '0')
    const formatted = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
      d.getUTCDate(),
    )} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
    return <time dateTime={value}>{formatted}</time>
  } catch {
    return <span>{value}</span>
  }
}

/** Render a UUID / id in monospace with select-all so the user can copy. */
export function MonoCode({ value }: { readonly value: string }): JSX.Element {
  return (
    <code className="select-all font-mono text-murmuration-code text-text-primary">{value}</code>
  )
}
